import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";

import { faBars } from "@fortawesome/pro-regular-svg-icons";

import { Avatar, ButtonIcon, Chip, Icon, Tooltip } from "@performyard/py-components";

import MobileDrawer from "./MobileDrawer";
import { NewIconSvg } from "./NewIconSvg";
import { SubNavbar } from "./SubNavbar";

import EmployeeSearch from "../../../containers/EmployeeSearch/EmployeeSearch";

import type { NavigationConfig } from "../../../api/hooks/defs/navigation";

import { buildPromotionalAdContext, getPromotionalUrl, shouldShowPromotionalAd } from "../mainNavConfig";
import type { MainNavItemConfig, NavItemConfig } from "../mainNavConfig";

const LEGACY_LOGO_FALLBACK_URL = "/static/img/py-logo/py-logo-and-text-medium-with-padding.png";
const LEGACY_MOBILE_LOGO_FALLBACK_URL = "/static/img/py-logo/py-logo-solo.png";

export type NavbarProps = {
	// Navigation config and computed state
	config: NavigationConfig;
	mainNavItems: MainNavItemConfig[];
	activeMainTab: string | null;
	activeNavItem: NavItemConfig | null;
	parentNavItem: NavItemConfig | null;
	currentSubNavItems: NavItemConfig[];

	// Server-computed nav-tag visibility
	isDirectoryVisible: boolean;

	// User data
	userEmail?: string;

	// Optional overrides
	gitBranch?: string | null;
	headerElement?: HTMLElement | null;
	subNavElement?: HTMLElement | null;
};

/**
 * Pure presentational Navbar component.
 * Accepts all data as props - performs no data fetching.
 * Can be used in Storybook with mock data.
 */
export const Navbar = ({
	config,
	mainNavItems,
	activeMainTab,
	activeNavItem,
	parentNavItem,
	currentSubNavItems,
	isDirectoryVisible,
	userEmail = "",
	gitBranch = null,
	headerElement: headerElementProp = null,
	subNavElement: subNavElementProp = null,
}: NavbarProps) => {
	// Mobile drawer state
	const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

	// Avatar dropdown state
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	// Portal targets - use props if provided, otherwise query DOM
	const [headerElement, setHeaderElement] = useState<HTMLElement | null>(
		headerElementProp || (() => document.getElementById("header"))
	);
	const [subNavElement, setSubNavElement] = useState<HTMLElement | null>(
		subNavElementProp || (() => document.getElementById("sub-nav-header"))
	);

	// Find portal elements if not provided as props
	useEffect(() => {
		if (!headerElementProp && !headerElement) {
			const element = document.getElementById("header");
			if (element) setHeaderElement(element);
		}
	}, [headerElementProp, headerElement]);

	useEffect(() => {
		if (!subNavElementProp && !subNavElement) {
			const element = document.getElementById("sub-nav-header");
			if (element) setSubNavElement(element);
		}
	}, [subNavElementProp, subNavElement]);

	// Set data attribute and background color on sub nav container based on active tab
	useEffect(() => {
		if (subNavElement && activeMainTab) {
			subNavElement.setAttribute("data-tab", activeMainTab);
			// Set background color - engagement tab is special
			if (activeMainTab === "engagement") {
				subNavElement.style.backgroundColor = "var(--py-primary-9)";
			} else {
				subNavElement.style.backgroundColor = "var(--py-neutral-2)";
			}
		} else if (subNavElement) {
			subNavElement.removeAttribute("data-tab");
			subNavElement.style.backgroundColor = "var(--py-neutral-2)";
		}
	}, [subNavElement, activeMainTab]);

	if (!headerElement) {
		return null;
	}

	const { isViewingSelf } = config;

	return (
		<>
			{/* Mobile Drawer */}
			<MobileDrawer isOpen={isMobileDrawerOpen} onClose={() => setIsMobileDrawerOpen(false)} />

			{/* Main Navbar - portals into #header */}
			{createPortal(
				<section id="react-navbar-content" role="banner" className="h-full">
					<div className="mx-auto flex h-full max-w-[1380px] items-center justify-between px-4 py-3">
						{/* Logo */}
						<a style={{ display: "block" }} href="/" aria-label="Go to home page">
							<img
								id="logo"
								src={config.logoUrl || LEGACY_LOGO_FALLBACK_URL}
								alt="Company Logo Dashboard"
							/>
							<img
								id="mobile-logo"
								src={config.mobileLogoUrl || LEGACY_MOBILE_LOGO_FALLBACK_URL}
								alt="Company Logo Dashboard"
							/>
						</a>

						{/* Git branch chip (dev environments only) */}
						{gitBranch && (
							<div className="ml-2 py-line-clamp-1 inline-block">
								<Chip label={gitBranch} color="red" size={24} />
							</div>
						)}

						{/* Mobile nav button */}
						<div className="flex h-10 items-center justify-center md:hidden">
							<ButtonIcon
								size={32}
								icon={faBars}
								dangerouslySetClassName="text-[--company-icon-color] bg-none border-none"
								onClick={() => setIsMobileDrawerOpen(true)}
								aria-label="Open navigation menu"
								type="button"
							/>
						</div>

						{/* Main navigation links */}
						<nav
							aria-label="Primary navigation"
							id="react-navbar-links"
							className="hidden items-center gap-4 md:flex"
						>
							{/* Employee search - only render if the Directory is visible to this company */}
							{isDirectoryVisible && <EmployeeSearch rootElementId={undefined} />}

							{mainNavItems.map((navItem) => {
								if (!navItem) return null;

								const isActive = activeMainTab === navItem.id;

								// Administration link should trigger full page load, not SPA navigation
								if (navItem.id === "administration") {
									return (
										<Tooltip asChild key={navItem.id} content={navItem.title} placement="bottom">
											<a
												href={navItem.href || "/"}
												className={`top-item ${isActive ? "opacity-100" : "opacity-50 transition-opacity duration-150 hover:opacity-100"}`}
												aria-label={navItem.title}
											>
												<Icon
													dangerouslySetClassName="text-[--company-icon-color]"
													icon={navItem.icon}
													size={20}
													aria-hidden="true"
												/>
											</a>
										</Tooltip>
									);
								}

								// Check if this is a promotional ad
								// Module ownership comes from the server-computed nav config, not a client-side
								// feature-flag query: the server builds these with the same
								// `.get(flag, False)` the Tornado navbar uses, so the two navbars agree, and
								// there's no window where a pending flag query makes an owner look like a
								// non-owner and renders a bogus purchase ad next to the real tab.
								const isPromotionalAd = shouldShowPromotionalAd(
									navItem.id,
									buildPromotionalAdContext(config)
								);

								// Synthetic promo nav items (development promotions) have no in-app route
								// behind them -- `href` is already the off-site promotional URL -- so they
								// render as the ad alone.
								if (navItem.isPromo) {
									return (
										<Tooltip asChild key={navItem.id} content={navItem.title} placement="bottom">
											<a
												href={navItem.href}
												target="_blank"
												rel="noopener noreferrer"
												className="top-item text-[--company-icon-color] opacity-50 transition-opacity duration-150 hover:opacity-100"
												aria-label={`${navItem.title} (New — opens in new window)`}
											>
												<div className="flex items-center">
													<Icon
														dangerouslySetClassName="text-[--company-icon-color]"
														icon={navItem.icon}
														size={20}
														aria-hidden="true"
													/>
													<NewIconSvg />
												</div>
											</a>
										</Tooltip>
									);
								}

								// Special case for performance tab when viewing another user
								if (navItem.id === "performance" && !isViewingSelf) {
									return (
										<Tooltip asChild key={navItem.id} content={navItem.title} placement="bottom">
											<NavLink
												to={navItem.href || "/"}
												className="top-item item-text text-caption font-bold text-[--company-icon-color] opacity-50 transition-opacity duration-150 hover:opacity-100"
												aria-label="Return to my dashboard"
											>
												<i>My Dashboard</i>
											</NavLink>
										</Tooltip>
									);
								}

								// Regular nav link
								const regularNavLink = (
									<Tooltip asChild content={navItem.title} placement="bottom">
										<NavLink
											to={navItem.href || "/"}
											className={`top-item ${isActive ? "opacity-100" : "opacity-50 transition-opacity duration-150 hover:opacity-100"}`}
											aria-label={navItem.title}
										>
											<Icon
												dangerouslySetClassName="text-[--company-icon-color]"
												icon={navItem.icon}
												size={20}
												aria-hidden="true"
											/>
										</NavLink>
									</Tooltip>
								);

								if (!isPromotionalAd) {
									return <React.Fragment key={navItem.id}>{regularNavLink}</React.Fragment>;
								}

								// Promotional ad with NEW icon - links to external page. Rendered
								// alongside the regular link, never instead of it, so a real tab
								// always keeps navigating: ads only fire for non-owners, whose module
								// tab the config would not have produced, but if one is ever present
								// the ad must not hijack it. Ad first, matching nav_bar.html's order.
								const promotionId = String(config.currentPromotion?._id ?? "");
								const promoUrl = getPromotionalUrl(
									navItem.id,
									userEmail,
									promotionId,
									config.currentPromotion
								);
								return (
									<React.Fragment key={navItem.id}>
										<Tooltip asChild content={navItem.title} placement="bottom">
											<a
												href={promoUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="top-item text-[--company-icon-color] opacity-50 transition-opacity duration-150 hover:opacity-100"
												aria-label={`${navItem.title} (New — opens in new window)`}
												onClick={(e) => {
													e.preventDefault();
													window.open(promoUrl, `${navItem.id}-ad-page`);
												}}
											>
												<div className="flex items-center">
													<Icon
														dangerouslySetClassName="text-[--company-icon-color]"
														icon={navItem.icon}
														size={20}
														aria-hidden="true"
													/>
													<NewIconSvg />
												</div>
											</a>
										</Tooltip>
										{regularNavLink}
									</React.Fragment>
								);
							})}

							{/* User Avatar dropdown */}
							<div
								className="relative"
								onMouseEnter={() => setIsDropdownOpen(true)}
								onMouseLeave={() => setIsDropdownOpen(false)}
								onFocus={() => setIsDropdownOpen(true)}
								onBlur={() => setIsDropdownOpen(false)}
							>
								<button
									type="button"
									className="cursor-pointer border-none bg-transparent"
									aria-label="User account menu"
									aria-haspopup="true"
									aria-expanded={isDropdownOpen}
								>
									<Avatar
										size={28}
										id={config.currentUserId}
										alt={`${config.currentUserName} profile menu`}
										dangerouslySetClassName="border-neutral-100 border-solid border border-2"
									/>
								</button>

								{/* Dropdown menu */}
								<div
									className={`absolute top-full right-0 z-10 mt-2 w-56 origin-top-right rounded-lg border border-neutral-3 bg-white shadow-lg transition-all duration-150 ${isDropdownOpen ? "visible scale-100 opacity-100" : "invisible scale-95 opacity-0"} `}
									role="menu"
									aria-label="User menu"
								>
									{/* Caret */}
									<div className="absolute -top-2 right-3 h-4 w-4 rotate-45 transform border-t border-l border-neutral-3 bg-white" />

									<div className="relative rounded-lg bg-white py-2">
										{/* Return to original user if impersonating */}
										{config.possessor && (
											<a
												className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
												href="/unpossess/"
												role="menuitem"
												aria-label={`Stop impersonating and return to ${config.possessor.name}`}
												onClick={() => {
													if (typeof (window as any).posthogUtils !== "undefined") {
														(window as any).posthogUtils.resetForUserSwitch(true);
													} else if (typeof (window as any).posthog !== "undefined") {
														(window as any).posthog.reset(true);
													}
												}}
											>
												Return to {config.possessor.name}
											</a>
										)}

										{/* Super User link for overlords */}
										{config.isOverlord && (
											<a
												className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
												href="/super-user/"
												role="menuitem"
												aria-label="Go to super user administration"
											>
												Super User
											</a>
										)}

										<div
											id="react-task-watcher"
											className="watcher-in-dropdown react-task-watcher"
										/>

										<a
											className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href={
												config.isEngagementOnly
													? "/settings/account/"
													: "/settings/notifications/"
											}
											role="menuitem"
											aria-label="Go to account settings"
										>
											Settings
										</a>
										<a
											className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href="#"
											role="menuitem"
											aria-label="Open Help Scout support beacon"
											onClick={(e) => {
												e.preventDefault();
												if (window.Beacon) {
													window.Beacon("open");
												}
											}}
										>
											Get Help
										</a>
										<a
											className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href="http://support.performyard.com"
											target="_blank"
											rel="noopener noreferrer"
											role="menuitem"
											aria-label="Get support (opens in new window)"
										>
											Support
										</a>
										<a
											className="line-clamp-2 block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href="/logout/"
											role="menuitem"
											aria-label={`Logout ${config.currentUserName}`}
										>
											Logout {config.currentUserName}
										</a>
									</div>
								</div>
							</div>
						</nav>
					</div>

					<div className="hidden md:block">
						{/* Sub Navbar - rendered below main nav */}
						<SubNavbar
							activeTabId={activeNavItem?.id || null}
							activeMainTab={activeMainTab}
							subNavItems={currentSubNavItems}
							ariaLabel={parentNavItem ? `${parentNavItem.title} navigation` : undefined}
						/>
					</div>
				</section>,
				headerElement
			)}
		</>
	);
};
